import React, { useState, type ReactNode } from 'react';
import { AlertTriangle, ArrowRight, FileText, Code2, X } from 'lucide-react';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { Link } from 'react-router-dom';
import { AdminActionBannerPanel, CopyableAddress } from './UsdWalletPayoutUx';
import {
  buildInternalReferenceLabel,
  buildPortalWithdrawalStory,
  buildProviderReferenceLabel,
  buildUsdLedgerDisplay,
  extractPayoutTracking,
  formatPayoutNetwork,
  getAdminActionBanner,
  getLedgerUxStatus,
  getLedgerUxStatusFromRow,
  isAutoPayoutMetadata,
  isPortalUsdWithdrawalReference,
  type LedgerMetadata,
  type UsdLedgerRowLike,
} from './usdWalletLedgerUtils';
import { ledgerLinkToAdminPath } from '../../../types/usdLedgerDisplay';
import type { UsdLedgerDisplayCategory } from '../../../types/usdLedgerDisplay';

const GREEN_AMOUNT = '#16a34a';
const RED_AMOUNT = '#dc2626';
const BADGE_COMPLETED_BG = '#dcfce7';
const BADGE_COMPLETED_TEXT = '#166534';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function moneyHero(v: unknown): string {
  return `$${num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function moneyFlow(v: unknown): string {
  return `$${num(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function signedMoneyHero(delta: number): string {
  const v = num(delta);
  if (v > 0.0000001) return `+${moneyHero(v)}`;
  if (v < -0.0000001) return `-${moneyHero(Math.abs(v))}`;
  return moneyHero(0);
}

function signedMoneyFlow(delta: number): string {
  const v = num(delta);
  if (v > 0.0000001) return `+${moneyFlow(v)}`;
  if (v < -0.0000001) return `-${moneyFlow(Math.abs(v))}`;
  return moneyFlow(0);
}

function statusDisplay(
  status: string,
  opts?: { type?: string; referenceType?: string; metadata?: LedgerMetadata },
): { label: string; bg: string; text: string } {
  if (opts?.type != null) {
    const ux = getLedgerUxStatus({
      type: opts.type,
      status,
      referenceType: opts.referenceType,
      metadata: opts.metadata,
    });
    return { label: ux.label, bg: ux.headerBg, text: ux.headerText };
  }
  const s = status.trim().toLowerCase();
  if (s === 'completed') {
    return { label: 'Completed', bg: BADGE_COMPLETED_BG, text: BADGE_COMPLETED_TEXT };
  }
  if (s === 'pending') {
    return { label: 'In progress', bg: '#fef3c7', text: '#92400e' };
  }
  if (s === 'cancelled') {
    return { label: 'Cancelled', bg: '#fee2e2', text: '#991b1b' };
  }
  if (s === 'failed') {
    return { label: 'Unsuccessful', bg: '#ffe4e6', text: '#9f1239' };
  }
  const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
  return { label, bg: 'hsl(var(--muted))', text: 'hsl(var(--muted-foreground))' };
}

function plainOwnerCategory(ownerTypeRaw: string): string {
  const t = ownerTypeRaw.trim().toLowerCase();
  if (t === 'shop') return 'Shop';
  if (t === 'agent') return 'Agent';
  if (t === 'super_agent') return 'Super agent';
  if (t === 'player') return 'Player';
  if (t === 'platform') return 'Platform';
  return ownerTypeRaw.replace(/_/g, ' ') || 'Account';
}

function shortOwnerName(primaryLabel: string): string {
  const parts = primaryLabel.split(' · ');
  return (parts[parts.length - 1] ?? primaryLabel).trim() || primaryLabel;
}

export type CounterpartResolved = {
  label: string;
  shortName: string;
  to?: string;
};

function CounterpartInline({
  name,
  resolved,
}: {
  name: string;
  resolved: CounterpartResolved | null;
}) {
  if (!name.trim()) return null;
  if (resolved?.to) {
    return (
      <Link
        to={resolved.to}
        className="font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
      >
        {resolved.shortName}
      </Link>
    );
  }
  return <strong className="text-foreground">{name}</strong>;
}

function buildWhatHappenedBody(params: {
  type: string;
  referenceType: string;
  ownerCategory: string;
  ownerShortName: string;
  ownerResolved: { label: string; to?: string } | null;
  counterpartName: string;
  counterpartResolved: CounterpartResolved | null;
  amountNet: number;
  netDelta: number;
  creditsAmount: number;
}): ReactNode {
  const rt = params.referenceType.trim().toLowerCase();
  const t = params.type.trim().toLowerCase();
  const owner = params.ownerShortName || params.ownerCategory;
  const counterpartName = params.counterpartName.trim();
  const credits = params.creditsAmount;
  const amount = moneyHero(Math.abs(params.amountNet || params.netDelta));

  const ownerInline =
    params.ownerResolved?.to != null ? (
      <Link
        to={params.ownerResolved.to}
        className="font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
      >
        {owner}
      </Link>
    ) : (
      <strong className="text-foreground">{owner}</strong>
    );

  if (rt === 'balance_to_credits_settlement' && counterpartName && credits > 0) {
    const creditsLabel = credits.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return (
      <>
        <strong>{params.ownerCategory}</strong> {ownerInline}{' '}
        <span className="text-foreground">received USD here</span> because{' '}
        <CounterpartInline name={counterpartName} resolved={params.counterpartResolved} /> used USD wallet balance to buy about{' '}
        <strong className="tabular-nums text-foreground">{creditsLabel} credits</strong> in the game wallet (credits
        follow the buyer&apos;s cost rate — see raw metadata for USD vs credits detail).
      </>
    );
  }

  if (rt === 'balance_to_credits_settlement' && counterpartName) {
    return (
      <>
        {ownerInline} received {amount} as the USD settlement when{' '}
        <CounterpartInline name={counterpartName} resolved={params.counterpartResolved} /> converted USD wallet balance to
        game credits.
      </>
    );
  }

  if (rt === 'balance_to_credits' && credits > 0) {
    const creditsLabel = credits.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return (
      <>
        This account paid about <strong className="tabular-nums text-foreground">{creditsLabel} credits</strong> worth
        (after cost rate). Their <strong className="text-foreground">USD balance went down</strong> on this row; an
        upstream wallet&apos;s credits funded the actual credit balance — see linked rows below when available.
      </>
    );
  }

  if (rt === 'balance_to_credits' || t === 'purchase_credits') {
    return (
      <>
        <strong>{amount}</strong> was converted from <strong>{owner}</strong>&apos;s USD wallet to game credits.
      </>
    );
  }

  if (t === 'deposit') {
    return (
      <>
        <strong>{amount}</strong> was added to <strong>{owner}</strong>&apos;s USD wallet.
      </>
    );
  }

  if (t === 'withdrawal') {
    return (
      <>
        <strong>{amount}</strong> was taken from <strong>{owner}</strong>&apos;s USD wallet.
      </>
    );
  }

  if (t === 'reversal' || t === 'adjustment') {
    return (
      <>
        Spendable USD balance for <strong>{owner}</strong> changed by{' '}
        <strong>{signedMoneyHero(params.netDelta)}</strong>.
      </>
    );
  }

  return (
    <>
      Spendable USD balance for <strong>{owner}</strong> changed by <strong>{signedMoneyHero(params.netDelta)}</strong>.
    </>
  );
}

function DetailTableRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border px-3 py-2 first:border-t-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-right text-xs font-medium text-foreground">{value}</div>
    </div>
  );
}

function auditSectionTitle(category: UsdLedgerDisplayCategory | null | undefined): string | null {
  if (category === 'transaction_reversal') return 'Reversal trace';
  if (category === 'refund') return 'Refund details';
  if (category === 'balance_correction_debit' || category === 'balance_correction_credit') {
    return 'Correction details';
  }
  return null;
}

function UsdLedgerAuditSection({
  displayCategory,
  audit,
  links,
}: {
  displayCategory?: UsdLedgerDisplayCategory | null;
  audit?: import('../../../types/usdLedgerDisplay').UsdLedgerDisplayAudit | null;
  links?: import('../../../types/usdLedgerDisplay').UsdLedgerDisplayLink[];
}) {
  const title = auditSectionTitle(displayCategory);
  if (!title || !audit) return null;

  const reasonText = audit.reason?.trim() || 'No reason recorded.';
  const initiatedBy = audit.initiatedBy?.displayName || '—';

  return (
    <section className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-900">{title}</h3>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-white/80 bg-white/90 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Reason</p>
          <p className="mt-1 whitespace-pre-wrap text-sm font-semibold text-slate-900">{reasonText}</p>
        </div>
        <div className="rounded-lg border border-white/80 bg-white/90 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Initiated by</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{initiatedBy}</p>
        </div>
        {audit.ticketId ? (
          <div className="rounded-lg border border-white/80 bg-white/90 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Ticket ID</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{audit.ticketId}</p>
          </div>
        ) : null}
        {displayCategory === 'transaction_reversal' && audit.originalTransactionId ? (
          <div className="rounded-lg border border-white/80 bg-white/90 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Original transaction ID</p>
            <p className="mt-1 break-all font-mono text-xs text-slate-800">{audit.originalTransactionId}</p>
          </div>
        ) : null}
        {displayCategory === 'transaction_reversal' && audit.reversalTransactionId ? (
          <div className="rounded-lg border border-white/80 bg-white/90 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Reversal transaction ID</p>
            <p className="mt-1 break-all font-mono text-xs text-slate-800">{audit.reversalTransactionId}</p>
          </div>
        ) : null}
        {(links ?? []).map((link) => {
          const to = ledgerLinkToAdminPath(link);
          return (
            <div key={`${link.kind}-${link.id}`} className="rounded-lg border border-white/80 bg-white/90 p-3 sm:col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{link.label}</p>
              {to ? (
                <Link to={to} className="mt-1 inline-block text-sm font-medium text-indigo-700 hover:underline">
                  {link.id}
                </Link>
              ) : (
                <p className="mt-1 break-all font-mono text-xs text-slate-800">{link.id}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export type UsdWalletActivityHeaderProps = {
  onClose: () => void;
  type: string;
  status: string;
  createdAt: unknown;
  netDelta: number;
  referenceType: string;
  referencePrimaryLabel: string;
  counterpartDisplayName: string;
  metadata?: LedgerMetadata;
  ledgerRow?: UsdLedgerRowLike;
  ownerPrimaryLabel?: string;
};

export type UsdWalletActivityViewProps = {
  type: string;
  status: string;
  createdAt: unknown;
  amountNet: number;
  amountGross?: number;
  netDelta: number;
  balanceNetBefore: number;
  balanceNetAfter: number;
  balanceGrossBefore?: number;
  balanceGrossAfter?: number;
  provider?: string;
  transactionId?: string;
  usdWalletId?: string;
  ownerId?: string;
  ledgerRow?: UsdLedgerRowLike;
  referenceType: string;
  referenceId: string;
  referencePrimaryLabel: string;
  /** Catalog blurb from `referenceTypeDescriptions` (legacy modal copy). */
  referenceDescription?: string | null;
  refLink?: { to: string; label: string } | null;
  ownerType: string;
  ownerPrimaryLabel: string;
  ownerResolved: { label: string; to?: string } | null;
  counterpartDisplayName: string;
  counterpartResolved?: CounterpartResolved | null;
  creditsAmount: number;
  metadata?: LedgerMetadata;
  withdrawalInfo?: {
    walletAddress?: string;
    coin?: string;
    network?: string;
    note?: string;
    requestedNetUsd?: number;
  };
  depositInfo?: {
    depositor?: string;
    fromAddress?: string;
    coin?: string;
    network?: string;
    note?: string;
  };
  creditLinkUsesOtherUsdRow?: boolean;
  extraSections?: ReactNode;
};

function buildExtendedB2cNarrative(params: {
  referenceType: string;
  ownerCategory: string;
  ownerPrimaryLabel: string;
  counterpartName: string;
  creditsAmount: number;
}): ReactNode | null {
  const rt = params.referenceType.trim().toLowerCase();
  const credits = params.creditsAmount;

  if (rt === 'balance_to_credits_settlement' && params.counterpartName && credits > 0) {
    return (
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        In the credit layer, the buyer&apos;s credits <strong className="text-foreground">went up</strong> and your
        tier&apos;s credits <strong className="text-foreground">went down</strong> to fund that purchase. This USD line is
        the settlement credit on <em>your</em> wallet; linked credit rows are looked up from the buyer&apos;s USD
        withdrawal for the same conversion.
      </p>
    );
  }

  if (rt === 'balance_to_credits' && credits > 0) {
    return (
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        An upstream wallet&apos;s credits funded the actual credit balance — see linked rows below when available.
      </p>
    );
  }

  return null;
}

export function UsdWalletActivityHeader({
  onClose,
  type,
  status,
  createdAt,
  netDelta,
  referenceType,
  counterpartDisplayName,
  metadata,
  ledgerRow,
  ownerPrimaryLabel,
}: UsdWalletActivityHeaderProps) {
  const fmt = useFormatDate();
  const badge = statusDisplay(status, { type, referenceType, metadata });
  const amountPositive = num(netDelta) >= 0;
  const rowForDisplay =
    ledgerRow ??
    ({
      type,
      status,
      referenceType,
      metadata,
      balanceNetBefore: num(netDelta) >= 0 ? 0 : Math.abs(netDelta),
      balanceNetAfter: Math.abs(netDelta),
    } as UsdLedgerRowLike);
  const ledgerDisplay = buildUsdLedgerDisplay(rowForDisplay, {
    ownerDisplayName: shortOwnerName(ownerPrimaryLabel ?? counterpartDisplayName),
  });

  return (
    <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-r from-white via-slate-50 to-white px-5 pb-4 pt-5">
      <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-indigo-200/40 blur-3xl" />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
        aria-label="Close"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>

      <div className="flex flex-wrap items-center gap-2 pr-8">
        <span
          className="inline-flex rounded px-2 py-0.5 text-[10px] font-medium leading-tight"
          style={{ backgroundColor: badge.bg, color: badge.text }}
        >
          {badge.label}
        </span>
        <span className="text-[11px] text-slate-500">{fmt(createdAt as string)}</span>
      </div>

      <p
        className="mt-2 text-lg font-medium tabular-nums tracking-tight"
        style={{ color: amountPositive ? GREEN_AMOUNT : RED_AMOUNT }}
      >
        {ledgerDisplay.amountLabel}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-800">{ledgerDisplay.subtitle}</p>
      <p className="mt-0.5 text-xs text-slate-600">{ledgerDisplay.detailSummary}</p>
      {ledgerDisplay.hasFee && ledgerDisplay.feeAmount != null ? (
        <p className="mt-1 text-xs text-slate-500">
          Fee: {moneyHero(ledgerDisplay.feeAmount)}
        </p>
      ) : null}
    </div>
  );
}

export default function UsdWalletActivityView({
  type,
  status,
  amountNet,
  amountGross = 0,
  netDelta,
  balanceNetBefore,
  balanceNetAfter,
  balanceGrossBefore = 0,
  balanceGrossAfter = 0,
  provider = '',
  transactionId = '',
  usdWalletId = '',
  ownerId = '',
  ledgerRow,
  referenceType,
  referenceId,
  referencePrimaryLabel,
  referenceDescription,
  refLink,
  ownerType,
  ownerPrimaryLabel,
  ownerResolved,
  counterpartDisplayName,
  counterpartResolved = null,
  creditsAmount,
  metadata,
  withdrawalInfo,
  depositInfo,
  creditLinkUsesOtherUsdRow,
  extraSections,
}: UsdWalletActivityViewProps) {
  const fmt = useFormatDate();
  const [activeTab, setActiveTab] = useState<'business' | 'developer'>('business');
  const ownerCategory = plainOwnerCategory(ownerType);
  const ownerShort = shortOwnerName(ownerPrimaryLabel);
  const amountPositive = num(netDelta) >= 0;
  const meta = metadata ?? {};
  const rowForDisplay =
    ledgerRow ??
    ({
      type,
      status,
      referenceType,
      referenceId,
      provider,
      transactionId,
      metadata: meta,
      amountNet,
      amountGross,
      balanceNetBefore,
      balanceNetAfter,
      balanceGrossBefore,
      balanceGrossAfter,
    } as UsdLedgerRowLike);
  const ledgerDisplay = buildUsdLedgerDisplay(rowForDisplay, {
    ownerDisplayName: shortOwnerName(ownerPrimaryLabel),
  });
  const providerReference = buildProviderReferenceLabel(rowForDisplay, meta);
  const internalReference = buildInternalReferenceLabel(referenceType, referenceId);
  const ledgerUx = getLedgerUxStatusFromRow(rowForDisplay);
  const adminBanner = getAdminActionBanner({ type, status, referenceType, metadata: meta });
  const portalWithdrawal =
    type.trim().toLowerCase() === 'withdrawal' && isPortalUsdWithdrawalReference(referenceType);
  const payoutTracking = extractPayoutTracking(meta);
  const requestedNet = num(
    withdrawalInfo?.requestedNetUsd ?? meta.requestedNetUsd ?? Math.abs(netDelta),
  );
  const payoutNetworkLabel = formatPayoutNetwork(
    String(withdrawalInfo?.network ?? meta.payoutNetwork ?? ''),
  );
  const portalStory =
    portalWithdrawal && isAutoPayoutMetadata(meta)
      ? buildPortalWithdrawalStory({
          ownerShortName: ownerShort,
          requestedNetUsd: requestedNet,
          network: payoutNetworkLabel,
          uxKind: ledgerUx.kind,
        })
      : null;
  const ownerRoleLabel = ownerRoleDetailLabel(ownerType);
  const showOwnerRow = ownerType.trim() !== '' && ownerType !== '—';

  const ownerValue =
    ownerResolved?.to != null ? (
      <Link to={ownerResolved.to} className="font-medium text-primary hover:underline">
        {ownerShort}
      </Link>
    ) : (
      ownerShort
    );

  const fraudFlagged = Boolean(
    (ledgerRow as { fraudFlagged?: boolean; fraudFlag?: unknown } | undefined)?.fraudFlagged ??
      (ledgerRow as { fraudFlag?: unknown } | undefined)?.fraudFlag,
  );
  const fraud = (ledgerRow as { fraudFlag?: {
    id?: string;
    ruleOrReason?: string;
    severity?: string;
    flaggedAt?: string | null;
  } } | undefined)?.fraudFlag ?? null;
  const fraudFlaggedAtLabel = fraud?.flaggedAt ? fmt(fraud.flaggedAt) : null;
  const extendedB2c = buildExtendedB2cNarrative({
    referenceType,
    ownerCategory,
    ownerPrimaryLabel,
    counterpartName: counterpartDisplayName,
    creditsAmount,
  });

  const whatHappenedBody = buildWhatHappenedBody({
    type,
    referenceType,
    ownerCategory,
    ownerShortName: ownerShort,
    ownerResolved,
    counterpartName: counterpartDisplayName,
    counterpartResolved,
    amountNet,
    netDelta,
    creditsAmount,
  });

  const showPlainLanguageB2c =
    (referenceType.trim().toLowerCase() === 'balance_to_credits_settlement' &&
      counterpartDisplayName.trim() &&
      creditsAmount > 0) ||
    (referenceType.trim().toLowerCase() === 'balance_to_credits' && creditsAmount > 0);

  const isWithdrawal = type.trim().toLowerCase() === 'withdrawal';
  const withdrawalAssetLine = [withdrawalInfo?.coin, payoutNetworkLabel || withdrawalInfo?.network]
    .filter(Boolean)
    .join(' · ');
  const withdrawalWallet = (withdrawalInfo?.walletAddress ?? '').trim();
  const withdrawalNote = (withdrawalInfo?.note ?? '').trim();

  const withdrawalDestTitle = (() => {
    const cp = counterpartDisplayName.trim();
    if (withdrawalAssetLine) return withdrawalAssetLine;
    const sentTo = cp.match(/^sent to\s+(.+)$/i);
    if (sentTo?.[1]) return sentTo[1].trim();
    if (cp && !/^external\s+destination$/i.test(cp)) return cp;
    return 'External payout';
  })();

  const withdrawalDestSubtitle = (() => {
    const parts: string[] = [];
    if (requestedNet > 0) parts.push(moneyHero(requestedNet));
    const cp = counterpartDisplayName.trim();
    const titleLower = withdrawalDestTitle.toLowerCase();
    if (cp && /^sent to /i.test(cp)) {
      const channel = cp.replace(/^sent to\s+/i, '').trim();
      if (channel && !titleLower.includes(channel.toLowerCase())) parts.push(cp);
    } else if (/^external/i.test(cp) && parts.length === 1) {
      parts.push('External wallet');
    }
    return parts.join(' · ');
  })();

  const counterpartDestination =
    counterpartResolved?.to != null ? (
      <Link
        to={counterpartResolved.to}
        className="mt-1 inline-block truncate text-sm font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
      >
        {counterpartResolved.shortName}
      </Link>
    ) : (
      <p className="mt-1 truncate text-sm font-semibold text-slate-900">
        {counterpartDisplayName || 'External destination'}
      </p>
    );

  return (
    <div className="space-y-4 bg-white p-1 text-slate-900">
      <div className="mb-1 border-b border-slate-200 px-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('business')}
            className={
              activeTab === 'business'
                ? 'inline-flex items-center gap-1.5 border-b-2 border-indigo-500 px-3 py-2 text-sm font-semibold text-indigo-600'
                : 'inline-flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700'
            }
          >
            <FileText className="h-4 w-4" />
            Business Flow
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('developer')}
            className={
              activeTab === 'developer'
                ? 'inline-flex items-center gap-1.5 border-b-2 border-indigo-500 px-3 py-2 text-sm font-semibold text-indigo-600'
                : 'inline-flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700'
            }
          >
            <Code2 className="h-4 w-4" />
            Developer Details
          </button>
        </div>
      </div>

      {activeTab === 'business' ? (
        <>
      {ledgerDisplay.hasFee ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Fee breakdown</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-600">
                {ledgerDisplay.isMoneyIn ? 'Amount received' : 'Gross amount'}
              </span>
              <span className="font-mono font-semibold text-slate-900">
                {moneyHero(ledgerDisplay.amountGross || amountGross || Math.abs(netDelta) + (ledgerDisplay.feeAmount ?? 0))}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-600">Platform fee</span>
              <span className="font-mono font-semibold text-rose-600">
                -{moneyHero(ledgerDisplay.feeAmount ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-2">
              <span className="font-medium text-slate-800">
                {ledgerDisplay.isMoneyIn ? 'Net added to wallet' : 'Net wallet impact'}
              </span>
              <span className="font-mono font-semibold text-slate-900">
                {moneyHero(ledgerDisplay.amountNet || amountNet || Math.abs(netDelta))}
              </span>
            </div>
          </div>
        </section>
      ) : null}

      {adminBanner ? (
        <div className="mt-2">
          <AdminActionBannerPanel banner={adminBanner} />
        </div>
      ) : null}

      {fraudFlagged ? (
        <section
          className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4"
          role="alert"
          aria-label="Fraud indicator warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-amber-950">Flagged by fraud indicator</h3>
              {fraud ? (
                <dl className="mt-3 space-y-2 text-sm">
                  {fraud.id ? (
                    <div>
                      <dt className="text-amber-900/80">Fraud Indicator ID</dt>
                      <dd className="font-mono text-xs text-amber-950">{fraud.id}</dd>
                    </div>
                  ) : null}
                  {fraud.ruleOrReason ? (
                    <div>
                      <dt className="text-amber-900/80">Rule / Reason</dt>
                      <dd className="text-amber-950">{fraud.ruleOrReason}</dd>
                    </div>
                  ) : null}
                  {fraud.severity ? (
                    <div>
                      <dt className="text-amber-900/80">Severity</dt>
                      <dd className="capitalize text-amber-950">{fraud.severity}</dd>
                    </div>
                  ) : null}
                  {fraudFlaggedAtLabel ? (
                    <div>
                      <dt className="text-amber-900/80">Flagged Time</dt>
                      <dd className="text-amber-950">{fraudFlaggedAtLabel}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : (
                <p className="mt-2 text-sm text-amber-900/90">
                  This transaction has been flagged for review.
                </p>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {portalStory ? (
        <section className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Summary</h3>
          <p className="text-sm leading-relaxed text-slate-800">{portalStory}</p>
        </section>
      ) : null}

      {activeTab === 'business' && showPlainLanguageB2c ? (
        <section className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800">
            What happened in plain language
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{whatHappenedBody}</p>
          {extendedB2c}
        </section>
      ) : null}

      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Transaction flow</h3>
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 p-2.5 sm:p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Source</p>
            {type === 'deposit' ? (
              counterpartResolved?.to && counterpartDisplayName ? (
                <Link
                  to={counterpartResolved.to}
                  className="mt-1 inline-block text-sm font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                >
                  {counterpartResolved.shortName}
                </Link>
              ) : (
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {counterpartDisplayName || 'Payment processor'}
                </p>
              )
            ) : ownerResolved?.to ? (
              <Link to={ownerResolved.to} className="mt-1 inline-block text-sm font-semibold text-indigo-700 hover:text-indigo-900 hover:underline">
                {ownerShort}
              </Link>
            ) : (
              <p className="mt-1 text-sm font-semibold text-slate-900">{ownerShort}</p>
            )}
            <p className="text-xs text-slate-600">{type === 'deposit' ? 'Payment processor' : ownerCategory}</p>
            {refLink ? (
              <Link
                to={refLink.to}
                className="mt-1 inline-block text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Ref: {refLink.label}
              </Link>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-center gap-1 pt-6">
            <ArrowRight className={amountPositive ? 'h-4 w-4 text-emerald-400' : 'h-4 w-4 text-rose-400'} />
            <span className={amountPositive ? 'rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-mono text-emerald-400' : 'rounded bg-rose-500/10 px-1.5 py-0.5 text-[11px] font-mono text-rose-400'}>
              {signedMoneyFlow(netDelta)}
            </span>
          </div>
          <div className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 p-2.5 sm:p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Destination</p>
            {type === 'deposit' ? (
              <>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900">{ownerShort}</p>
                <p className="text-xs text-slate-600">{ownerCategory}</p>
              </>
            ) : isWithdrawal ? (
              <>
                {counterpartResolved?.to ? (
                  <Link
                    to={counterpartResolved.to}
                    className="mt-1 inline-block truncate text-sm font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                  >
                    {withdrawalDestTitle}
                  </Link>
                ) : (
                  <p className="mt-1 text-sm font-semibold text-slate-900">{withdrawalDestTitle}</p>
                )}
                {withdrawalDestSubtitle ? (
                  <p className="mt-0.5 text-xs tabular-nums text-slate-600">{withdrawalDestSubtitle}</p>
                ) : null}
                {withdrawalWallet ? (
                  <CopyableAddress address={withdrawalWallet} compact />
                ) : null}
                {withdrawalNote ? (
                  <p className="mt-1.5 line-clamp-2 text-[11px] leading-snug text-slate-600">
                    <span className="text-slate-500">Note:</span> {withdrawalNote}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {counterpartDestination}
                <p className="text-xs text-slate-600">
                  {counterpartResolved?.label.split(' · ')[0] ?? 'Buyer / external'}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Balance impact</h3>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Before</p>
            <p className="mt-1 font-mono text-2xl font-semibold">{moneyHero(balanceNetBefore)}</p>
          </div>
          <span className={amountPositive ? 'rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-mono font-semibold text-emerald-600' : 'rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-mono font-semibold text-rose-600'}>
            {signedMoneyFlow(netDelta)}
          </span>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">After</p>
            <p className="mt-1 font-mono text-2xl font-semibold">{moneyHero(balanceNetAfter)}</p>
          </div>
        </div>
      </section>

      {type === 'deposit' ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Deposit source details</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Depositor</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{depositInfo?.depositor || '—'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">From wallet / source</p>
              <p className="mt-1 break-all font-mono text-xs text-slate-800">{depositInfo?.fromAddress || '—'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Coin / Token</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{depositInfo?.coin || '—'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Network</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{depositInfo?.network || '—'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Note</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{depositInfo?.note || '—'}</p>
            </div>
          </div>
        </section>
      ) : null}

      {(creditsAmount > 0 || refLink) ? (
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Related system activity</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {creditsAmount > 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">In-game credits</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{creditsAmount.toLocaleString()} Credits</p>
              </div>
            ) : null}
            {refLink ? (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Gateway payment</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{referencePrimaryLabel}</p>
                <Link to={refLink.to} className="mt-2 inline-block text-xs text-indigo-700 hover:text-indigo-900 hover:underline">
                  Open related {refLink.label.toLowerCase()}
                </Link>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <UsdLedgerAuditSection
        displayCategory={ledgerDisplay.displayCategory}
        audit={ledgerDisplay.audit}
        links={ledgerDisplay.links}
      />

      <section className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-indigo-800">Source &amp; reason</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-white/80 bg-white/90 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Reason</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{ledgerDisplay.reasonLabel}</p>
          </div>
          <div className="rounded-lg border border-white/80 bg-white/90 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Funding source</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{ledgerDisplay.sourceLabel}</p>
          </div>
          {providerReference ? (
            <div className="rounded-lg border border-white/80 bg-white/90 p-3 sm:col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Provider reference</p>
              <p className="mt-1 break-all font-mono text-xs text-slate-800">{providerReference}</p>
            </div>
          ) : null}
          {internalReference ? (
            <div className="rounded-lg border border-white/80 bg-white/90 p-3 sm:col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Internal reference</p>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {refLink ? (
                  <Link to={refLink.to} className="text-indigo-700 hover:text-indigo-900 hover:underline">
                    {internalReference}
                  </Link>
                ) : (
                  internalReference
                )}
              </p>
            </div>
          ) : null}
        </div>
      </section>
        </>
      ) : null}

      {activeTab === 'developer' ? (
        <section className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Identifiers</p>
          <DetailTableRow label="transactionId" value={<span className="break-all font-mono text-[11px]">{transactionId || '—'}</span>} />
          <DetailTableRow label="provider" value={<span className="font-mono text-[11px]">{provider || '—'}</span>} />
          <DetailTableRow label="referenceType" value={<span className="font-mono text-[11px]">{referenceType || '—'}</span>} />
          <DetailTableRow label="referenceId" value={<span className="break-all font-mono text-[11px]">{referenceId || '—'}</span>} />
          <DetailTableRow label="usdWalletId" value={<span className="break-all font-mono text-[11px]">{usdWalletId || '—'}</span>} />
          <DetailTableRow label="ownerType" value={<span className="font-mono text-[11px]">{ownerType || '—'}</span>} />
          <DetailTableRow label="ownerId" value={<span className="break-all font-mono text-[11px]">{ownerId || '—'}</span>} />

          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Ledger status (system)</p>
          <p className="break-all font-mono text-[11px] text-slate-700">{status || '—'}</p>
          {isAutoPayoutMetadata(meta) ? (
            <>
              <p className="mt-3 text-[10px] text-slate-500">Payout phase (system)</p>
              <p className="break-all font-mono text-[11px] text-slate-700">
                {String(meta.payoutPhase ?? (meta.payout as LedgerMetadata)?.payoutPhase ?? '—')}
              </p>
              {payoutTracking.externalTransactionId ? (
                <>
                  <p className="mt-3 text-[10px] text-slate-500">Provider tracking ID</p>
                  <p className="break-all font-mono text-[11px] text-slate-700">
                    {payoutTracking.externalTransactionId}
                  </p>
                </>
              ) : null}
              {payoutTracking.provider ? (
                <>
                  <p className="mt-3 text-[10px] text-slate-500">Payout provider</p>
                  <p className="break-all font-mono text-[11px] text-slate-700">{payoutTracking.provider}</p>
                </>
              ) : null}
            </>
          ) : null}
          <p className="mt-3 text-[10px] text-slate-500">Internal link code</p>
          <p className="break-all font-mono text-[11px] text-slate-700">{referenceId || '—'}</p>
          <p className="mt-3 text-[10px] text-slate-500">Category code</p>
          <p className="break-all font-mono text-[11px] text-slate-700">{referenceType || '—'}</p>
          {referenceDescription ? <p className="mt-3 text-xs text-slate-600">{referenceDescription}</p> : null}
          {showOwnerRow ? (
            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-[10px] text-slate-500">{ownerRoleLabel}</p>
              <div className="mt-1 text-sm text-slate-800">{ownerValue}</div>
            </div>
          ) : null}
          {creditLinkUsesOtherUsdRow ? (
            <p className="mt-2 text-[11px] text-slate-600">
              credit lines are loaded from <span className="font-mono">source_usd_wallet_txn_id</span>.
            </p>
          ) : null}
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">metadata (raw JSON)</p>
          <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white p-3 font-mono text-[10px] leading-relaxed text-slate-800">
            {Object.keys(meta).length > 0 ? JSON.stringify(meta, null, 2) : '—'}
          </pre>
        </section>
      ) : null}

      {activeTab === 'business' && extendedB2c && !showPlainLanguageB2c ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">{extendedB2c}</div>
      ) : null}
      {extraSections}
    </div>
  );
}

function ownerRoleDetailLabel(ownerTypeRaw: string): string {
  return plainOwnerCategory(ownerTypeRaw);
}
